import type { Action, ThunkAction } from "@reduxjs/toolkit"
import { configureStore } from "@reduxjs/toolkit"
import { setupListeners } from "@reduxjs/toolkit/query"
import { authApi } from "../features/auth/service"
import { callTypeApi } from "../features/call-type/service.ts"
import { factTrendsApi } from "../features/fact-trends-report/service"
import { featureApi } from "../features/feature/service"
import { fileFeatureApi } from "../features/file-feature/service"
import { analyticsApi } from "../features/home/service"
import { operatorReportApi } from "../features/operator-report/service"
import { organizationApi } from "../features/organization/organization-service"
import { organizationGroupApi } from "../features/organization-group/organization-group-service"
import { departmentApi } from "../features/department/department-service"
import { settingsApi } from "../features/settings/service.ts"
import { topReportApi } from "../features/top-report/service"
import { dataSourcesApi } from "../features/data-sources/service"
import { featureWeightApi } from "../features/feature-weight/feature-weight-service"

// `combineSlices` automatically combines the reducers using
// their `reducerPath`s, therefore we no longer need to call `combineReducers`.
// Infer the `RootState` type from the root reducer

// The store setup is wrapped in `makeStore` to allow reuse
// when setting up tests that need the same store config
export const makeStore = () => {
  const store = configureStore({
    reducer: {
      [authApi.reducerPath]: authApi.reducer,
      [analyticsApi.reducerPath]: analyticsApi.reducer,
      [operatorReportApi.reducerPath]: operatorReportApi.reducer,
      [topReportApi.reducerPath]: topReportApi.reducer,
      [factTrendsApi.reducerPath]: factTrendsApi.reducer,
      [featureApi.reducerPath]: featureApi.reducer,
      [fileFeatureApi.reducerPath]: fileFeatureApi.reducer,
      [callTypeApi.reducerPath]: callTypeApi.reducer,
      [settingsApi.reducerPath]: settingsApi.reducer,
      [organizationApi.reducerPath]: organizationApi.reducer,
      [organizationGroupApi.reducerPath]: organizationGroupApi.reducer,
      [departmentApi.reducerPath]: departmentApi.reducer,
      [dataSourcesApi.reducerPath]: dataSourcesApi.reducer,
      [featureWeightApi.reducerPath]: featureWeightApi.reducer,
    },
    // Adding the api middleware enables caching, invalidation, polling,
    // and other useful features of `rtk-query`.
    middleware: getDefaultMiddleware => {
      return getDefaultMiddleware().concat(
        authApi.middleware,
        analyticsApi.middleware,
        operatorReportApi.middleware,
        topReportApi.middleware,
        factTrendsApi.middleware,
        featureApi.middleware,
        fileFeatureApi.middleware,
        callTypeApi.middleware,
        settingsApi.middleware,
        organizationApi.middleware,
        organizationGroupApi.middleware,
        departmentApi.middleware,
        dataSourcesApi.middleware,
        featureWeightApi.middleware,
      )
    },
  })
  // configure listeners using the provided defaults
  // optional, but required for `refetchOnFocus`/`refetchOnReconnect` behaviors
  setupListeners(store.dispatch)
  return store
}
export type RootState = ReturnType<typeof store.getState>
export const store = makeStore()

// Infer the type of `store`
export type AppStore = typeof store
// Infer the `AppDispatch` type from the store itself
export type AppDispatch = AppStore["dispatch"]
export type AppThunk<ThunkReturnType = void> = ThunkAction<ThunkReturnType, RootState, unknown, Action>
